import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { useUser, useAuth } from "@clerk/react";
import { useTranslation } from "react-i18next";
import { getPoolStats } from "@workspace/api-client-react";
import { safeSetItem } from "@/lib/safeStorage";
import { Heart, Users, DollarSign, ChevronRight, Globe, Settings, Trophy, Leaf, Info, type LucideIcon } from "lucide-react";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import { useState, useEffect, useRef } from "react";
import { motion, useReducedMotion, useInView, useMotionValue, useTransform, animate } from "framer-motion";
import { fadeUp, fadeIn, staggerContainer, staggerItem, instant } from "@/lib/animations";

const SAGE = "#A8B5A2";
const TERRACOTTA = "#D9B8A3";
const CLAY = "#E8DAB2";

const pillBtn: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: "6px",
  padding: "13px 28px",
  borderRadius: "50px",
  fontSize: "14px",
  fontWeight: 300,
  letterSpacing: "0.04em",
  cursor: "pointer",
  transition: "all 0.3s ease",
  border: "1px solid transparent",
};

function CountUp({ target, format }: { target: number; format: (n: number) => string }) {
  const reduce = useReducedMotion();
  const ref = useRef<HTMLSpanElement>(null);
  const isInView = useInView(ref, { once: true, amount: 0.5 });
  const count = useMotionValue(reduce ? target : 0);
  const display = useTransform(count, (v) => format(Math.round(v)));

  useEffect(() => {
    if (!isInView || reduce) return;
    const ctrl = animate(count, target, { duration: 1.6, ease: "easeOut" });
    return ctrl.stop;
  }, [isInView, target, count, reduce]);

  return <motion.span ref={ref}>{reduce ? format(target) : display}</motion.span>;
}

function StatCard({ icon: Icon, label, targetNum, format }: {
  icon: LucideIcon;
  label: string;
  targetNum: number;
  format: (n: number) => string;
}) {
  return (
    <div
      className="glass-card"
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "8px",
        padding: "24px",
        borderRadius: "16px",
        cursor: "default",
      }}
    >
      <div style={{ padding: "10px", borderRadius: "50%", background: "rgba(168,181,162,0.15)", border: "1px solid rgba(168,181,162,0.25)" }}>
        <Icon style={{ width: "20px", height: "20px", color: SAGE }} />
      </div>
      <div style={{ fontSize: "30px", fontWeight: 200, color: "#0a0a0a", letterSpacing: "-0.03em" }}>
        <CountUp target={targetNum} format={format} />
      </div>
      <div style={{ fontSize: "11px", fontWeight: 300, color: "rgba(10,10,10,0.45)", letterSpacing: "0.08em", textTransform: "uppercase" }}>{label}</div>
    </div>
  );
}

export default function Landing() {
  const { isSignedIn } = useUser();
  const { isLoaded } = useAuth();
  const { t } = useTranslation();
  const [, navigate] = useLocation();
  const [mouse, setMouse] = useState({ x: 0, y: 0 });
  const [scrollY, setScrollY] = useState(0);
  const reduce = useReducedMotion();

  const fadeUpV = reduce ? instant : fadeUp;
  const fadeInV = reduce ? instant : fadeIn;
  const staggerC = reduce ? instant : staggerContainer;
  const staggerI = reduce ? instant : staggerItem;

  useEffect(() => {
    function onMouseMove(e: MouseEvent) { setMouse({ x: e.clientX, y: e.clientY }); }
    function onScroll() { setScrollY(window.scrollY); }
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  const { data: stats } = useQuery({
    queryKey: ["poolStats"],
    queryFn: () => getPoolStats(),
  });

  function navigateAsDonor() {
    if (isSignedIn) navigate("/donor");
    else { safeSetItem("pendingRole", "donor"); navigate("/sign-up"); }
  }

  function navigateAsDebtor() {
    if (isSignedIn) navigate("/debtor");
    else { safeSetItem("pendingRole", "debtor"); navigate("/sign-up"); }
  }

  return (
    <div style={{ minHeight: "100vh", background: "hsl(var(--background))", fontFamily: "'Inter', sans-serif", fontWeight: 300 }}>
      <SiteNav />

      {/* Hero / Mission */}
      <section
        style={{
          position: "relative",
          overflow: "hidden",
          background: "rgba(252,252,252,0.88)",
          padding: "100px 20px 80px",
          minHeight: "580px",
        }}
      >
        {/* Sunrise/hope landscape — ghost texture */}
        <img
          src="https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1600&q=80&fit=crop&auto=format"
          alt="" aria-hidden loading="lazy"
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", opacity: 0.13, filter: "grayscale(0.15)", pointerEvents: "none" }}
        />
        <div aria-hidden style={{
          position: "absolute",
          inset: "-25%",
          background: "radial-gradient(ellipse 80% 60% at 50% 40%, rgba(168,181,162,0.07) 0%, transparent 55%)",
          transform: `translateY(${scrollY * 0.35}px)`,
          pointerEvents: "none",
        }} />

        <svg
          aria-hidden
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none", overflow: "visible" }}
        >
          <g style={{ transition: "transform 0.8s ease", transform: `translate(${mouse.x * 0.012}px, ${mouse.y * 0.012 + scrollY * -0.08}px)` }}>
            <circle cx="50%" cy="40%" r="300" fill="none" stroke="#A8B5A2" strokeWidth="0.75" opacity="0.13" />
          </g>
          <g style={{ transition: "transform 0.8s ease", transform: `translate(${mouse.x * 0.015}px, ${mouse.y * 0.015 + scrollY * -0.03}px)` }}>
            <circle cx="55%" cy="48%" r="430" fill="none" stroke="#A8B5A2" strokeWidth="0.4" opacity="0.05" />
          </g>
          <g style={{ transition: "transform 0.8s ease", transform: `translate(${mouse.x * 0.025}px, ${mouse.y * 0.025 + scrollY * -0.05}px)` }}>
            <circle cx="70%" cy="55%" r="180" fill="none" stroke="#D9B8A3" strokeWidth="0.5" opacity="0.10" />
          </g>
          <g style={{ transition: "transform 0.8s ease", transform: `translate(${mouse.x * 0.018}px, ${mouse.y * 0.018 + scrollY * -0.06}px)` }}>
            <circle cx="30%" cy="62%" r="115" fill="none" stroke="#A8B5A2" strokeWidth="0.5" opacity="0.08" />
          </g>
          <g style={{ transition: "transform 0.8s ease", transform: `translate(${mouse.x * 0.008}px, ${mouse.y * 0.008}px)` }}>
            <line x1="5%" y1="5%" x2="95%" y2="95%" stroke="#E8DAB2" strokeWidth="0.4" opacity="0.07" />
            <line x1="95%" y1="5%" x2="5%" y2="95%" stroke="#A8B5A2" strokeWidth="0.4" opacity="0.07" />
          </g>
        </svg>

        <motion.div
          variants={staggerC}
          initial="hidden"
          animate="visible"
          style={{ position: "relative", maxWidth: "760px", margin: "0 auto", textAlign: "center" }}
        >
          {/* Hero badge with shimmer */}
          <motion.div variants={fadeInV}>
            <div
              className="hero-badge-shimmer"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                padding: "6px 16px",
                borderRadius: "50px",
                background: "rgba(10,10,10,0.05)",
                border: "1px solid rgba(10,10,10,0.10)",
                color: "rgba(10,10,10,0.55)",
                fontSize: "11px",
                fontWeight: 300,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                marginBottom: "32px",
              }}
            >
              {t("hero.badge")}
            </div>
          </motion.div>

          <motion.h1
            variants={fadeUpV}
            style={{
              fontSize: "clamp(42px, 7vw, 76px)",
              fontWeight: 200,
              color: "#0a0a0a",
              lineHeight: 1.06,
              letterSpacing: "-0.04em",
              marginBottom: "24px",
            }}
          >
            {t("hero.headline1")}<br />
            <span style={{ color: SAGE }}>{t("hero.headline2")}</span> {t("hero.headline3")}
          </motion.h1>

          <motion.p
            variants={fadeUpV}
            style={{
              fontSize: "17px",
              fontWeight: 300,
              color: "rgba(10,10,10,0.55)",
              marginBottom: "40px",
              maxWidth: "520px",
              margin: "0 auto 40px",
              lineHeight: 1.7,
              letterSpacing: "0.01em",
            }}
          >
            {t("hero.subtext")}
          </motion.p>

          <motion.div variants={fadeUpV} style={{ display: "flex", flexWrap: "wrap", gap: "12px", justifyContent: "center" }}>
            <button
              className="btn-shimmer"
              style={{ ...pillBtn, background: "#0a0a0a", color: "#ffffff", border: "1px solid rgba(10,10,10,0.9)" }}
              onClick={navigateAsDonor}
              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.boxShadow = `0 0 0 3px rgba(168,181,162,0.35)`; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.boxShadow = "none"; }}
            >
              {t("hero.donateNow")} <ChevronRight aria-hidden="true" style={{ width: "16px", height: "16px" }} />
            </button>
            <button
              className="btn-shimmer"
              style={{ ...pillBtn, background: "transparent", color: "rgba(10,10,10,0.60)", border: "1px solid rgba(10,10,10,0.20)" }}
              onClick={navigateAsDebtor}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLButtonElement).style.background = "rgba(10,10,10,0.05)";
                (e.currentTarget as HTMLButtonElement).style.borderColor = `rgba(168,181,162,0.55)`;
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.background = "transparent";
                (e.currentTarget as HTMLButtonElement).style.borderColor = "rgba(10,10,10,0.20)";
              }}
            >
              {t("hero.iNeedHelp")}
            </button>
          </motion.div>
        </motion.div>

        {/* Impact Stats — count-up animation on scroll into view */}
        {stats && (stats.totalDonors > 0 || stats.totalErased > 0) && (
          <motion.div
            variants={staggerC}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            style={{
              position: "relative",
              marginTop: "72px",
              maxWidth: "880px",
              margin: "72px auto 0",
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
              gap: "16px",
              padding: "0 20px",
            }}
          >
            <motion.div variants={staggerI}>
              <StatCard
                icon={DollarSign}
                label={t("stats.debtErased")}
                targetNum={stats.totalErased / 1000}
                format={(n) => `$${n.toFixed(0)}k`}
              />
            </motion.div>
            <motion.div variants={staggerI}>
              <StatCard
                icon={Users}
                label={t("stats.peopleHelped")}
                targetNum={stats.debtorsHelped}
                format={(n) => String(n)}
              />
            </motion.div>
            <motion.div variants={staggerI}>
              <StatCard
                icon={Heart}
                label={t("stats.donors")}
                targetNum={stats.totalDonors}
                format={(n) => String(n)}
              />
            </motion.div>
          </motion.div>
        )}

        {stats && stats.totalDonors === 0 && stats.totalErased === 0 && (
          <motion.div
            variants={fadeUpV}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            style={{
              position: "relative",
              marginTop: "72px",
              maxWidth: "560px",
              margin: "72px auto 0",
              textAlign: "center",
              padding: "32px 24px",
              borderRadius: "20px",
              background: "rgba(168,181,162,0.08)",
              border: "1px solid rgba(168,181,162,0.2)",
            }}
          >
            <div style={{ marginBottom: "16px", display: "flex", justifyContent: "center" }}><Leaf size={28} color={SAGE} /></div>
            <p style={{ fontSize: "17px", fontWeight: 200, color: "#0a0a0a", lineHeight: 1.7, marginBottom: "8px", letterSpacing: "-0.01em" }}>
              {t("stats.missionCallout")}
            </p>
            <p style={{ fontSize: "12px", fontWeight: 300, color: "rgba(10,10,10,0.45)", letterSpacing: "0.04em" }}>
              {t("stats.launchingSoon")}
            </p>
          </motion.div>
        )}
      </section>

      {/* Quick links to each section page */}
      <section style={{ padding: "64px 20px", background: "#0a0a0a", position: "relative", overflow: "hidden" }}>
        <div aria-hidden style={{
          position: "absolute", inset: 0,
          background: "radial-gradient(ellipse 55% 45% at 60% 50%, rgba(168,181,162,0.05) 0%, transparent 65%)",
          pointerEvents: "none",
        }} />
        <div style={{ position: "relative", maxWidth: "1100px", margin: "0 auto" }}>
          <motion.p
            variants={fadeInV}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            style={{ fontSize: "11px", fontWeight: 400, letterSpacing: "0.12em", textTransform: "uppercase", color: SAGE, marginBottom: "40px", textAlign: "center" }}
          >
            Explore DebtBridge
          </motion.p>
          <motion.div
            variants={staggerC}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.15 }}
            style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "16px" }}
          >
            {[
              { path: "/impact", Icon: Globe, title: "Our Impact", desc: "How we're changing lives and why debt relief matters" },
              { path: "/how-it-works", Icon: Settings, title: "How It Works", desc: "From donation to debt settlement — the full process" },
              { path: "/pools", Icon: Heart, title: "Browse Pools", desc: "Open fundraising pools you can donate to right now" },
              { path: "/leaderboard", Icon: Trophy, title: "Leaderboard", desc: "Top donors making the biggest difference" },
              { path: "/join", Icon: Leaf, title: "Join", desc: "Donate or apply for relief — start your journey here" },
              { path: "/about", Icon: Info, title: "About Us", desc: "Our story, mission, and nonprofit structure" },
            ].map(({ path, Icon, title, desc }) => (
              <motion.button
                key={path}
                variants={staggerI}
                onClick={() => navigate(path)}
                style={{
                  textAlign: "left",
                  padding: "24px 20px",
                  borderRadius: "14px",
                  border: "1px solid rgba(255,255,255,0.07)",
                  background: "rgba(255,255,255,0.03)",
                  cursor: "pointer",
                  display: "flex",
                  flexDirection: "column",
                  gap: "10px",
                }}
                whileHover={reduce ? {} : {
                  borderColor: `${SAGE}50`,
                  background: "rgba(168,181,162,0.07)",
                  y: -2,
                  transition: { duration: 0.18 },
                }}
              >
                <Icon size={20} color={SAGE} />
                <div>
                  <div style={{ fontSize: "14px", fontWeight: 400, color: "#ffffff", marginBottom: "4px", letterSpacing: "-0.01em" }}>{title}</div>
                  <div style={{ fontSize: "12px", fontWeight: 300, color: "rgba(255,255,255,0.40)", lineHeight: 1.55 }}>{desc}</div>
                </div>
              </motion.button>
            ))}
          </motion.div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
